import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { HeroSettings, HeroSlide, OfferBanner, HeroTag, Coupon, ThemeCustomization } from '../types';
import { HeroSlider } from './HeroSlider';
import {
  Image,
  Upload,
  Sparkles,
  Tag,
  Plus,
  Trash2,
  Check,
  Flame,
  Eye,
  Gift,
  Layers,
  Palette,
  Clock,
  Calendar,
  Percent,
  Euro,
  Sliders,
  DollarSign,
  Volume2,
  Edit2,
  Smartphone,
  Monitor,
  Info,
  ArrowUp,
  ArrowDown,
  Utensils
} from 'lucide-react';
import {
  playModernSuccessChime,
  playServiceBellChime,
  playMarimbaChord,
  playSwooshConfirm
} from '../utils/audio';

export const HeroBannerStudio: React.FC = () => {
  const {
    heroSlides,
    saveHeroSlides,
    addHeroSlide,
    updateHeroSlide,
    deleteHeroSlide,
    heroSettings,
    saveHeroSettings,
    offerBanners,
    saveOfferBanners,
    coupons,
    saveCoupons,
    themeCustomization,
    saveThemeCustomization,
    orderSoundTheme,
    saveOrderSoundTheme,
    currentView
  } = useApp();
  const { lang, t } = useLanguage();

  const activeSubTab =
    currentView === 'staff-banners-offers'
      ? 'offers'
      : currentView === 'staff-banners-coupons'
      ? 'coupons'
      : currentView === 'staff-banners-hero'
      ? 'hero'
      : 'branding';

  // Local state for Hero
  const [hero, setHero] = useState<HeroSettings>(heroSettings);
  const [newTagIcon, setNewTagIcon] = useState('🔥');
  const [newTagFi, setNewTagFi] = useState('');
  const [newTagEn, setNewTagEn] = useState('');

  // Local state for Multi-Slide Carousel Studio
  const [isSlideModalOpen, setIsSlideModalOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState<HeroSlide | null>(null);
  const [slideType, setSlideType] = useState<'brand_hero' | 'promo_offer' | 'dish_highlight' | 'custom_graphic'>('promo_offer');
  const [slideBadge, setSlideBadge] = useState('🔥 SPECIAL DEAL');
  const [slideTitleEn, setSlideTitleEn] = useState('');
  const [slideTitleFi, setSlideTitleFi] = useState('');
  const [slideSubtitleEn, setSlideSubtitleEn] = useState('');
  const [slideSubtitleFi, setSlideSubtitleFi] = useState('');
  const [slideDiscountCode, setSlideDiscountCode] = useState('');
  const [slideImageUrl, setSlideImageUrl] = useState('');
  const [slideCtaLabelEn, setSlideCtaLabelEn] = useState('');
  const [slideCtaLabelFi, setSlideCtaLabelFi] = useState('');
  const [slideTags, setSlideTags] = useState<HeroTag[]>([]);
  const [slideNewTagIcon, setSlideNewTagIcon] = useState('🍕');
  const [slideNewTagText, setSlideNewTagText] = useState('');

  const handleOpenAddSlide = () => {
    setEditingSlide(null);
    setSlideType('promo_offer');
    setSlideBadge('🔥 SPECIAL OFFER');
    setSlideTitleEn('');
    setSlideTitleFi('');
    setSlideSubtitleEn('');
    setSlideSubtitleFi('');
    setSlideDiscountCode('');
    setSlideImageUrl('');
    setSlideCtaLabelEn('Apply Voucher');
    setSlideCtaLabelFi('Käytä kuponki');
    setSlideTags([]);
    setIsSlideModalOpen(true);
  };

  const handleOpenEditSlide = (s: HeroSlide) => {
    setEditingSlide(s);
    setSlideType(s.slide_type);
    setSlideBadge(s.badge_text || '');
    setSlideTitleEn(s.title_en);
    setSlideTitleFi(s.title_fi);
    setSlideSubtitleEn(s.subtitle_en);
    setSlideSubtitleFi(s.subtitle_fi);
    setSlideDiscountCode(s.discount_code || '');
    setSlideImageUrl(s.image_url || '');
    setSlideCtaLabelEn(s.cta_label_en || '');
    setSlideCtaLabelFi(s.cta_label_fi || '');
    setSlideTags(s.tags || []);
    setIsSlideModalOpen(true);
  };

  const handleSaveSlide = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slideTitleEn.trim()) return;

    if (editingSlide) {
      await updateHeroSlide(editingSlide.id, {
        slide_type: slideType,
        badge_text: slideBadge,
        title_en: slideTitleEn,
        title_fi: slideTitleFi || slideTitleEn,
        title_sv: slideTitleEn,
        subtitle_en: slideSubtitleEn,
        subtitle_fi: slideSubtitleFi || slideSubtitleEn,
        subtitle_sv: slideSubtitleEn,
        discount_code: slideDiscountCode || undefined,
        image_url: slideImageUrl || undefined,
        cta_label_en: slideCtaLabelEn || undefined,
        cta_label_fi: slideCtaLabelFi || undefined,
        cta_label_sv: slideCtaLabelEn || undefined,
        tags: slideTags
      });
    } else {
      await addHeroSlide({
        slide_type: slideType,
        badge_text: slideBadge,
        title_en: slideTitleEn,
        title_fi: slideTitleFi || slideTitleEn,
        title_sv: slideTitleEn,
        subtitle_en: slideSubtitleEn,
        subtitle_fi: slideSubtitleFi || slideSubtitleEn,
        subtitle_sv: slideSubtitleEn,
        discount_code: slideDiscountCode || undefined,
        image_url: slideImageUrl || undefined,
        cta_label_en: slideCtaLabelEn || undefined,
        cta_label_fi: slideCtaLabelFi || undefined,
        cta_label_sv: slideCtaLabelEn || undefined,
        tags: slideTags,
        is_active: true,
        sort_order: (heroSlides || []).length + 1
      });
    }
    setIsSlideModalOpen(false);
    playModernSuccessChime();
  };

  const handleToggleSlideActive = async (id: string, current: boolean) => {
    await updateHeroSlide(id, { is_active: !current });
    playModernSuccessChime();
  };

  const handleDeleteSlide = async (id: string) => {
    if (window.confirm('Are you sure you want to remove this slide from the carousel?')) {
      await deleteHeroSlide(id);
      playModernSuccessChime();
    }
  };

  const handleSlideImageFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        setSlideImageUrl(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleAddSlideTag = () => {
    if (!slideNewTagText.trim()) return;
    setSlideTags([...slideTags, { icon: slideNewTagIcon || '✨', text_en: slideNewTagText, text_fi: slideNewTagText, text_sv: slideNewTagText }]);
    setSlideNewTagText('');
  };

  const handleRemoveSlideTag = (idx: number) => {
    setSlideTags(slideTags.filter((_, i) => i !== idx));
  };

  // Local state for Offer Banners
  const [banners, setBanners] = useState<OfferBanner[]>(offerBanners);
  const [isAddingOffer, setIsAddingOffer] = useState(false);
  const [editingOffer, setEditingOffer] = useState<OfferBanner | null>(null);
  const [offerBadge, setOfferBadge] = useState('SPECIAL DEAL 🔥');
  const [offerTitleEn, setOfferTitleEn] = useState('');
  const [offerTitleFi, setOfferTitleFi] = useState('');
  const [offerSubtitleEn, setOfferSubtitleEn] = useState('');
  const [offerSubtitleFi, setOfferSubtitleFi] = useState('');
  const [offerCode, setOfferCode] = useState('');
  const [offerDisplayMode, setOfferDisplayMode] = useState<'minimal_ribbon' | 'standard_card' | 'image_only'>('minimal_ribbon');
  const [offerGradient, setOfferGradient] = useState('linear-gradient(135deg, #e05a36, #b91c1c)');
  const [offerImageUrl, setOfferImageUrl] = useState('');

  // Local state for Coupons Engine
  const [couponList, setCouponList] = useState<Coupon[]>(coupons);
  const [isAddingCoupon, setIsAddingCoupon] = useState(false);
  const [newCouponCode, setNewCouponCode] = useState('');
  const [newCouponTitle, setNewCouponTitle] = useState('');
  const [newCouponDesc, setNewCouponDesc] = useState('');
  const [newDiscountType, setNewDiscountType] = useState<'percentage' | 'fixed'>('fixed');
  const [newDiscountVal, setNewDiscountVal] = useState(3.5);
  const [newMinOrder, setNewMinOrder] = useState(15);
  const [newMaxDiscount, setNewMaxDiscount] = useState(10);
  const [newFestivalTag, setNewFestivalTag] = useState('');
  const [newStartTime, setNewStartTime] = useState('');
  const [newEndTime, setNewEndTime] = useState('');
  const [newValidDays, setNewValidDays] = useState<number[]>([1, 2, 3, 4, 5]);

  // Color Palette Presets
  const colorPresets = [
    { name: 'Warm Terracotta / Nordic Copper', hex: '#E05A36' },
    { name: 'Helsinki Basil Emerald', hex: '#059669' },
    { name: 'Golden Amber Hearth', hex: '#D97706' },
    { name: 'Nordic Lingonberry Crimson', hex: '#DC2626' },
    { name: 'Arctic Deep Navy', hex: '#2563EB' },
    { name: 'Royal Artisan Amethyst', hex: '#7C3AED' }
  ];

  // Cover image file upload handler
  const handleImageFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) {
          setHero(prev => ({ ...prev, cover_image_url: reader.result as string }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Offer banner image file upload handler
  const handleOfferImageFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) {
          setOfferImageUrl(reader.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddTag = () => {
    if (!newTagEn.trim() && !newTagFi.trim()) return;
    const tag: HeroTag = {
      icon: newTagIcon || '✨',
      text_fi: newTagFi.trim() || newTagEn.trim(),
      text_en: newTagEn.trim() || newTagFi.trim(),
      text_sv: newTagEn.trim() || newTagFi.trim()
    };
    setHero(prev => ({ ...prev, tags: [...prev.tags, tag] }));
    setNewTagFi('');
    setNewTagEn('');
  };

  const handleRemoveTag = (index: number) => {
    setHero(prev => ({ ...prev, tags: prev.tags.filter((_, idx) => idx !== index) }));
  };

  const handleSaveHero = async () => {
    await saveHeroSettings(hero);
    alert('Hero banner & cover settings saved successfully!');
  };

  // Live Color Change
  const handleColorChange = async (hex: string) => {
    await saveThemeCustomization({
      primary_color: hex,
      accent_style: 'custom'
    });
  };

  // Offer Banner operations
  const handleToggleOffer = async (id: string) => {
    const updated = banners.map(b => b.id === id ? { ...b, is_active: !b.is_active } : b);
    setBanners(updated);
    await saveOfferBanners(updated);
  };

  const handleDeleteOffer = async (id: string) => {
    const updated = banners.filter(b => b.id !== id);
    setBanners(updated);
    await saveOfferBanners(updated);
  };

  const handleOpenCreateOffer = () => {
    setEditingOffer(null);
    setOfferBadge('SPECIAL DEAL 🔥');
    setOfferTitleEn('');
    setOfferTitleFi('');
    setOfferSubtitleEn('');
    setOfferSubtitleFi('');
    setOfferCode('');
    setOfferDisplayMode('minimal_ribbon');
    setOfferGradient('linear-gradient(135deg, #e05a36, #b91c1c)');
    setOfferImageUrl('');
    setIsAddingOffer(true);
  };

  const handleOpenEditOffer = (banner: OfferBanner) => {
    setEditingOffer(banner);
    setOfferBadge(banner.badge || 'SPECIAL DEAL 🔥');
    setOfferTitleEn(banner.title_en || '');
    setOfferTitleFi(banner.title_fi || '');
    setOfferSubtitleEn(banner.subtitle_en || '');
    setOfferSubtitleFi(banner.subtitle_fi || '');
    setOfferCode(banner.discount_code || '');
    setOfferDisplayMode(banner.display_mode || 'minimal_ribbon');
    setOfferGradient(banner.bg_gradient || 'linear-gradient(135deg, #e05a36, #b91c1c)');
    setOfferImageUrl(banner.image_url || '');
    setIsAddingOffer(true);
  };

  const handleSaveOffer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingOffer) {
      // Update existing offer
      const updatedOffer: OfferBanner = {
        ...editingOffer,
        badge: offerBadge.trim() || 'SPECIAL DEAL 🔥',
        title_en: offerTitleEn.trim() || 'Special Restaurant Offer',
        title_fi: offerTitleFi.trim() || offerTitleEn.trim() || 'Erikoistarjous ravintolassa',
        title_sv: offerTitleEn.trim() || 'Specialerbjudande',
        subtitle_en: offerSubtitleEn.trim() || 'Available for table dine-in orders.',
        subtitle_fi: offerSubtitleFi.trim() || 'Voimassa pöytätilauksiin.',
        subtitle_sv: offerSubtitleEn.trim() || 'Gäller bordsbeställningar.',
        discount_code: offerCode.trim() || undefined,
        bg_gradient: offerGradient,
        display_mode: offerDisplayMode,
        image_url: offerImageUrl.trim() || undefined
      };
      const updated = banners.map(b => b.id === editingOffer.id ? updatedOffer : b);
      setBanners(updated);
      await saveOfferBanners(updated);
    } else {
      // Create new offer
      const newOffer: OfferBanner = {
        id: `offer_${Date.now()}`,
        badge: offerBadge.trim() || 'SPECIAL DEAL 🔥',
        title_en: offerTitleEn.trim() || 'Special Restaurant Offer',
        title_fi: offerTitleFi.trim() || offerTitleEn.trim() || 'Erikoistarjous ravintolassa',
        title_sv: offerTitleEn.trim() || 'Specialerbjudande',
        subtitle_en: offerSubtitleEn.trim() || 'Available for table dine-in orders.',
        subtitle_fi: offerSubtitleFi.trim() || 'Voimassa pöytätilauksiin.',
        subtitle_sv: offerSubtitleEn.trim() || 'Gäller bordsbeställningar.',
        discount_code: offerCode.trim() || undefined,
        bg_gradient: offerGradient,
        display_mode: offerDisplayMode,
        image_url: offerImageUrl.trim() || undefined,
        is_active: true
      };
      const updated = [newOffer, ...banners];
      setBanners(updated);
      await saveOfferBanners(updated);
    }
    setIsAddingOffer(false);
    setEditingOffer(null);
  };

  // Coupon Engine operations
  const handleToggleCoupon = async (id: string) => {
    const updated = couponList.map(c => c.id === id ? { ...c, is_active: !c.is_active } : c);
    setCouponList(updated);
    await saveCoupons(updated);
  };

  const handleDeleteCoupon = async (id: string) => {
    const updated = couponList.filter(c => c.id !== id);
    setCouponList(updated);
    await saveCoupons(updated);
  };

  const handleCreateCoupon = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = newCouponCode.trim().toUpperCase();
    if (!code) return;

    const newCpn: Coupon = {
      id: `cpn_${Date.now()}`,
      code,
      title: newCouponTitle.trim() || code,
      description: newCouponDesc.trim() || `${newDiscountType === 'percentage' ? `${newDiscountVal}%` : `${newDiscountVal} €`} alennus tilauksesta`,
      discount_type: newDiscountType,
      discount_value: Number(newDiscountVal),
      min_order_amount: Number(newMinOrder) || 0,
      max_discount_amount: newDiscountType === 'percentage' ? Number(newMaxDiscount) : undefined,
      valid_days: newValidDays,
      valid_start_time: newStartTime || undefined,
      valid_end_time: newEndTime || undefined,
      customer_type: 'all',
      festival_tag: newFestivalTag.trim() || undefined,
      is_active: true
    };

    const updated = [newCpn, ...couponList];
    setCouponList(updated);
    await saveCoupons(updated);
    setIsAddingCoupon(false);
    setNewCouponCode('');
    setNewCouponTitle('');
    setNewCouponDesc('');
    setNewFestivalTag('');
  };

  const toggleDaySelection = (dayNum: number) => {
    if (newValidDays.includes(dayNum)) {
      setNewValidDays(newValidDays.filter(d => d !== dayNum));
    } else {
      setNewValidDays([...newValidDays, dayNum]);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Top Navigation Sub-Tabs */}
      {/* Dynamic Page Header based on Sidebar Selection */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          background: 'var(--bg-secondary)',
          padding: '16px 20px',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-subtle)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--accent-copper-light)',
              color: 'var(--accent-copper)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {activeSubTab === 'branding' && <Palette size={20} />}
            {activeSubTab === 'offers' && <Gift size={20} />}
            {activeSubTab === 'coupons' && <Percent size={20} />}
            {activeSubTab === 'hero' && <Image size={20} />}
          </div>
          <div>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 800 }}>
              {activeSubTab === 'branding' && 'Live Brand Color & Order Sound Theme'}
              {activeSubTab === 'offers' && `Promotional Offer Banners (${banners.length})`}
              {activeSubTab === 'coupons' && `Smart Condition-Based Coupons (${couponList.length})`}
              {activeSubTab === 'hero' && 'Hero Cover Banner & Badges'}
            </h2>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {activeSubTab === 'branding' && 'Live accent color palette and custom order sound chime'}
              {activeSubTab === 'offers' && 'Promotional ribbon announcements visible on the diner menu'}
              {activeSubTab === 'coupons' && 'Create timing, day, spend, and festival based promo codes'}
              {activeSubTab === 'hero' && 'Customize header cover photo, title, and highlights'}
            </div>
          </div>
        </div>

        {activeSubTab === 'hero' && (
          <button className="btn-primary" onClick={handleSaveHero}>
            <Check size={16} />
            <span>Save Hero Banner</span>
          </button>
        )}
      </div>

      {/* 1. BRAND COLOR PALETTE CUSTOMIZER */}
      {activeSubTab === 'branding' && (
        <div
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-xl)',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}
        >
          <div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Palette size={20} color="var(--accent-copper)" />
              <span>Verkkosovelluksen Pääväri (Live Brand Color)</span>
            </h3>
            <p style={{ fontSize: '0.84rem', color: 'var(--text-muted)', marginTop: '4px' }}>
              Valitse tai aseta ravintolasi pääväri. Muutos päivittyy heti live-tilassa kaikkiin nappeihin, ostoskoripillereihin ja ilmoituksiin!
            </p>
          </div>

          {/* Presets Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            {colorPresets.map(preset => {
              const isSelected = themeCustomization.primary_color.toLowerCase() === preset.hex.toLowerCase();
              return (
                <div
                  key={preset.hex}
                  onClick={() => handleColorChange(preset.hex)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: 'var(--bg-card)',
                    border: `2px solid ${isSelected ? preset.hex : 'var(--border-subtle)'}`,
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    transition: 'all var(--transition-fast)'
                  }}
                >
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: preset.hex,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      flexShrink: 0
                    }}
                  />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>{preset.name}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{preset.hex}</div>
                  </div>
                  {isSelected && <Check size={16} color={preset.hex} style={{ marginLeft: 'auto' }} />}
                </div>
              );
            })}
          </div>

          {/* Custom Color Picker Input */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              background: 'var(--bg-card)',
              padding: '16px 20px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-subtle)'
            }}
          >
            <label style={{ fontWeight: 700, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Mukautettu Värivalitsin (Custom Hex Picker):</span>
              <input
                type="color"
                value={themeCustomization.primary_color}
                onChange={e => handleColorChange(e.target.value)}
                style={{ width: '40px', height: '40px', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              />
            </label>
            <input
              type="text"
              className="form-input"
              style={{ width: '120px', fontWeight: 800 }}
              value={themeCustomization.primary_color}
              onChange={e => handleColorChange(e.target.value)}
            />
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              Aktiivinen väri tallentuu ja tulee voimaan välittömästi.
            </span>
          </div>

          {/* ORDER SUBMISSION SOUND THEMES */}
          <div style={{ marginTop: '14px', borderTop: '1px solid var(--border-subtle)', paddingTop: '20px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Volume2 size={20} color="var(--accent-copper)" />
              <span>Tilauksen Lähetyksen Äänitehoste (Order Sound Chime)</span>
            </h3>
            <p style={{ fontSize: '0.84rem', color: 'var(--text-muted)', marginTop: '4px' }}>
              Valitse äänimerkki, joka toistetaan asiakkaan vahvistaessa tilauksen. Voit testata äänen suoraan painikkeesta!
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px', marginTop: '14px' }}>
              {[
                {
                  id: 'cash_register',
                  name: '1. Apple Pay / Modern Cash Ding',
                  desc: 'Puhdas ja selkeä nouseva kaksoisäänimerkki (Suositeltu)',
                  play: playModernSuccessChime
                },
                {
                  id: 'service_bell',
                  name: '2. Klassinen Ravintolakello',
                  desc: 'Aito metallinen tiskikellon kilahdus (Service Bell)',
                  play: playServiceBellChime
                },
                {
                  id: 'marimba_chord',
                  name: '3. Pohjoinen Marimba-sointu',
                  desc: 'Pehmeä, melodinen puusoittimen sointu',
                  play: playMarimbaChord
                },
                {
                  id: 'subtle_swoosh',
                  name: '4. Futuristinen Vahvistus',
                  desc: 'Moderni pehmeä pyyhkäisy ja kuittausääni',
                  play: playSwooshConfirm
                }
              ].map(snd => {
                const isSelected = orderSoundTheme === snd.id;
                return (
                  <div
                    key={snd.id}
                    style={{
                      background: 'var(--bg-card)',
                      border: `2px solid ${isSelected ? 'var(--accent-copper)' : 'var(--border-subtle)'}`,
                      borderRadius: 'var(--radius-md)',
                      padding: '14px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      cursor: 'pointer'
                    }}
                    onClick={() => saveOrderSoundTheme(snd.id as any)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontWeight: 800, fontSize: '0.9rem', color: isSelected ? 'var(--accent-copper)' : 'var(--text-primary)' }}>
                        {snd.name}
                      </div>
                      {isSelected && <Check size={16} color="var(--accent-copper)" />}
                    </div>

                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.35 }}>
                      {snd.desc}
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ padding: '4px 10px', fontSize: '0.75rem', gap: '5px' }}
                        onClick={e => {
                          e.stopPropagation();
                          snd.play();
                        }}
                      >
                        <Volume2 size={13} />
                        <span>Kuuntele ääni 🔊</span>
                      </button>

                      {isSelected ? (
                        <span style={{ fontSize: '0.75rem', color: 'var(--accent-copper)', fontWeight: 800, alignSelf: 'center', marginLeft: 'auto' }}>
                          ✓ Valittu
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="btn-primary"
                          style={{ padding: '4px 10px', fontSize: '0.75rem', marginLeft: 'auto' }}
                          onClick={() => saveOrderSoundTheme(snd.id as any)}
                        >
                          Valitse
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* 2. PROMOTIONAL OFFERS & MINIMAL RIBBON BANNER */}
      {activeSubTab === 'offers' && (
        <div
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-xl)',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}
        >
          {/* Header & Create Button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Gift size={20} color="var(--accent-copper)" />
                <span>Promotional Offer Banners ({banners.length})</span>
              </h3>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                Display enticing discounts, lunch deals, or graphic promotional posters to dining customers.
              </p>
            </div>

            <button className="btn-primary" onClick={handleOpenCreateOffer}>
              <Plus size={16} />
              <span>Create New Banner</span>
            </button>
          </div>

          {/* 📐 RECOMMENDED BANNER DIMENSIONS GUIDELINES (Mobile & Desktop) */}
          <div
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-lg)',
              padding: '18px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 800, fontSize: '0.92rem', color: 'var(--accent-copper)' }}>
              <Sparkles size={16} />
              <span>📐 Recommended Graphic Banner Dimensions & Aspect Ratios</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px' }}>
              {/* Mobile Size */}
              <div
                style={{
                  background: 'var(--bg-secondary)',
                  padding: '14px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 800, fontSize: '0.86rem', color: 'var(--text-primary)' }}>
                  <Smartphone size={16} color="var(--accent-copper)" />
                  <span>Mobile Screen Match (2.5 : 1)</span>
                </div>
                <div style={{ fontSize: '0.95rem', fontWeight: 900, color: 'var(--accent-copper)', marginTop: '2px' }}>
                  750 × 300 px <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>(or 1080 × 430 px HD)</span>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.35 }}>
                  Ideal for smartphone diner views. Clean horizontal banner without taking too much vertical screen space.
                </div>
              </div>

              {/* Desktop Size */}
              <div
                style={{
                  background: 'var(--bg-secondary)',
                  padding: '14px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 800, fontSize: '0.86rem', color: 'var(--text-primary)' }}>
                  <Monitor size={16} color="var(--accent-copper)" />
                  <span>Desktop & Tablet Wide (10 : 3)</span>
                </div>
                <div style={{ fontSize: '0.95rem', fontWeight: 900, color: 'var(--accent-copper)', marginTop: '2px' }}>
                  1200 × 360 px <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>(or 1600 × 480 px 2K)</span>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.35 }}>
                  Ultra-wide panoramic format for iPad POS, tablet menus, and widescreen desktop monitors.
                </div>
              </div>

              {/* Image-Only Poster */}
              <div
                style={{
                  background: 'var(--bg-secondary)',
                  padding: '14px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 800, fontSize: '0.86rem', color: 'var(--text-primary)' }}>
                  <Image size={16} color="var(--accent-copper)" />
                  <span>Image-Only Poster (16 : 9)</span>
                </div>
                <div style={{ fontSize: '0.95rem', fontWeight: 900, color: 'var(--accent-copper)', marginTop: '2px' }}>
                  800 × 450 px <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>(Standard HD)</span>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.35 }}>
                  Direct pre-designed Canva/Photoshop artwork with graphics, badges, and offer text embedded in the image.
                </div>
              </div>
            </div>

            <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Info size={14} color="var(--accent-copper)" />
              <span><strong>Supported Formats:</strong> WEBP, PNG, JPG (under 5MB). Keep core text centered with 20px padding from edges.</span>
            </div>
          </div>

          {/* Offers List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {banners.map(banner => (
              <div
                key={banner.id}
                style={{
                  background: banner.bg_gradient || 'linear-gradient(135deg, var(--accent-copper), #b91c1c)',
                  color: '#fff',
                  borderRadius: 'var(--radius-md)',
                  padding: '14px 18px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px',
                  opacity: banner.is_active ? 1 : 0.6,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0, flex: 1 }}>
                  {banner.image_url ? (
                    <img
                      src={banner.image_url}
                      alt={banner.title_en}
                      style={{
                        width: '64px',
                        height: '42px',
                        borderRadius: 'var(--radius-xs)',
                        objectFit: 'cover',
                        border: '1px solid rgba(255,255,255,0.3)',
                        flexShrink: 0
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '42px',
                        height: '42px',
                        borderRadius: 'var(--radius-xs)',
                        background: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <Gift size={20} color="#fff" />
                    </div>
                  )}

                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          background: 'rgba(255,255,255,0.22)',
                          padding: '2px 7px',
                          borderRadius: '3px',
                          fontSize: '0.7rem',
                          fontWeight: 800
                        }}
                      >
                        {banner.badge}
                      </span>

                      <span
                        style={{
                          background: 'rgba(0,0,0,0.3)',
                          padding: '2px 7px',
                          borderRadius: '3px',
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          textTransform: 'uppercase'
                        }}
                      >
                        {banner.display_mode === 'image_only'
                          ? '🖼️ Image Poster'
                          : banner.display_mode === 'standard_card'
                          ? '🃏 Card Banner'
                          : '⚡ Minimal Ribbon'}
                      </span>

                      <span style={{ fontWeight: 800, fontSize: '0.95rem' }}>{banner.title_en}</span>

                      {banner.discount_code && (
                        <span style={{ background: '#fff', color: '#000', padding: '2px 8px', borderRadius: '3px', fontWeight: 800, fontSize: '0.72rem' }}>
                          Code: {banner.discount_code}
                        </span>
                      )}
                    </div>

                    {banner.subtitle_en && (
                      <div style={{ fontSize: '0.8rem', opacity: 0.9, marginTop: '4px' }}>
                        {banner.subtitle_en}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    className="btn-secondary"
                    style={{
                      background: 'rgba(255,255,255,0.95)',
                      color: '#000',
                      padding: '5px 12px',
                      fontSize: '0.78rem',
                      fontWeight: 700
                    }}
                    onClick={() => handleOpenEditOffer(banner)}
                    title="Edit banner details and image"
                  >
                    <Edit2 size={13} />
                    <span>Edit</span>
                  </button>

                  <button
                    className="btn-secondary"
                    style={{
                      background: banner.is_active ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.5)',
                      color: '#fff',
                      padding: '5px 10px',
                      fontSize: '0.75rem'
                    }}
                    onClick={() => handleToggleOffer(banner.id)}
                  >
                    {banner.is_active ? 'Active' : 'Hidden'}
                  </button>

                  <button
                    className="btn-danger"
                    style={{ padding: '5px 8px' }}
                    onClick={() => handleDeleteOffer(banner.id)}
                    title="Delete offer banner"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Modal: Create / Edit Offer */}
          {isAddingOffer && (
            <div className="modal-overlay" onClick={() => setIsAddingOffer(false)}>
              <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: '580px' }}>
                <div className="modal-header">
                  <div className="modal-title">
                    {editingOffer ? 'Edit Promotional Offer Banner' : 'Create New Promotional Banner'}
                  </div>
                  <button className="modal-close-btn" onClick={() => setIsAddingOffer(false)}>✕</button>
                </div>

                <form onSubmit={handleSaveOffer}>
                  <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div className="form-group">
                      <label className="form-label" style={{ fontWeight: 800 }}>Display Layout Style</label>
                      <select
                        className="form-input"
                        value={offerDisplayMode}
                        onChange={e => setOfferDisplayMode(e.target.value as any)}
                      >
                        <option value="minimal_ribbon">⚡ Minimalist Ribbon (Compact text strip for diners)</option>
                        <option value="standard_card">🃏 Standard Graphic Card (Title + Subtitle + Badge + Code)</option>
                        <option value="image_only">🖼️ Image-Only Full Poster (Direct artwork upload)</option>
                      </select>
                    </div>

                    {/* Banner Image Upload & Preview */}
                    <div className="form-group">
                      <label className="form-label" style={{ fontWeight: 800 }}>
                        Banner Graphic Photo (Mobile: 750×300px | Desktop: 1200×360px)
                      </label>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <label
                          className="btn-secondary"
                          style={{
                            cursor: 'pointer',
                            padding: '8px 14px',
                            background: 'var(--accent-copper)',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.82rem'
                          }}
                        >
                          <Upload size={15} />
                          <span>Upload Image from Device</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleOfferImageFileUpload}
                            style={{ display: 'none' }}
                          />
                        </label>

                        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>or paste URL:</span>
                        <input
                          type="url"
                          className="form-input"
                          style={{ flex: 1, minWidth: '180px' }}
                          value={offerImageUrl}
                          onChange={e => setOfferImageUrl(e.target.value)}
                          placeholder="https://images.unsplash.com/..."
                        />
                      </div>

                      {offerImageUrl && (
                        <div style={{ marginTop: '10px', position: 'relative', borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--border-subtle)' }}>
                          <img
                            src={offerImageUrl}
                            alt="Banner Preview"
                            style={{ width: '100%', maxHeight: '140px', objectFit: 'cover' }}
                          />
                          <button
                            type="button"
                            onClick={() => setOfferImageUrl('')}
                            style={{
                              position: 'absolute',
                              top: '6px',
                              right: '6px',
                              background: 'rgba(0,0,0,0.7)',
                              color: '#fff',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              fontSize: '12px'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                      <div className="form-group">
                        <label className="form-label" style={{ fontWeight: 800 }}>Campaign Badge Tag</label>
                        <input
                          type="text"
                          className="form-input"
                          value={offerBadge}
                          onChange={e => setOfferBadge(e.target.value)}
                          placeholder="E.g. LUNCH SPECIAL 🔥"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label" style={{ fontWeight: 800 }}>Voucher / Coupon Code (Optional)</label>
                        <input
                          type="text"
                          className="form-input"
                          value={offerCode}
                          onChange={e => setOfferCode(e.target.value)}
                          placeholder="E.g. LUNCH1490"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label" style={{ fontWeight: 800 }}>Title (English) *</label>
                      <input
                        type="text"
                        required
                        className="form-input"
                        value={offerTitleEn}
                        onChange={e => setOfferTitleEn(e.target.value)}
                        placeholder="E.g. Lunch Special 11–14: Any Pizza only 14.90 €"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" style={{ fontWeight: 800 }}>Title (Finnish - Otsikko)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={offerTitleFi}
                        onChange={e => setOfferTitleFi(e.target.value)}
                        placeholder="Esim. Lounastarjous klo 11–14: Kaikki pizzat vain 14,90 €"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" style={{ fontWeight: 800 }}>Subtitle Description (English)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={offerSubtitleEn}
                        onChange={e => setOfferSubtitleEn(e.target.value)}
                        placeholder="E.g. Includes fresh side salad and organic coffee."
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" style={{ fontWeight: 800 }}>Subtitle Description (Finnish)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={offerSubtitleFi}
                        onChange={e => setOfferSubtitleFi(e.target.value)}
                        placeholder="Esim. Sisältää raikkaan salaatin ja luomukahvin."
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" style={{ fontWeight: 800 }}>Background Gradient Theme</label>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {[
                          { name: 'Nordic Fire', gradient: 'linear-gradient(135deg, #e05a36, #b91c1c)' },
                          { name: 'Emerald Forest', gradient: 'linear-gradient(135deg, #059669, #065f46)' },
                          { name: 'Amber Glow', gradient: 'linear-gradient(135deg, #d97706, #b45309)' },
                          { name: 'Midnight Purple', gradient: 'linear-gradient(135deg, #7c3aed, #4c1d95)' },
                          { name: 'Charcoal Minimal', gradient: 'linear-gradient(135deg, #27272a, #09090b)' }
                        ].map(g => (
                          <button
                            key={g.name}
                            type="button"
                            onClick={() => setOfferGradient(g.gradient)}
                            style={{
                              background: g.gradient,
                              color: '#fff',
                              padding: '6px 12px',
                              borderRadius: 'var(--radius-xs)',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              border: offerGradient === g.gradient ? '2px solid #fff' : '1px solid rgba(255,255,255,0.2)'
                            }}
                          >
                            {g.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn-secondary" onClick={() => setIsAddingOffer(false)}>Cancel</button>
                    <button type="submit" className="btn-primary">
                      {editingOffer ? 'Save Changes' : 'Create Banner'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 3. SMART COUPON ENGINE (ADMIN STUDIO) */}
      {activeSubTab === 'coupons' && (
        <div
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-xl)',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Percent size={20} color="var(--accent-copper)" />
                <span>Kuponkien & Alennuskoodien Hallinta (Smart Coupons)</span>
              </h3>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                Luo kuponkeja tilaussumman, viikonpäivän, kellonajan, festivaalin tai asiakastyypin mukaan.
              </p>
            </div>

            <button className="btn-primary" onClick={() => setIsAddingCoupon(true)}>
              <Plus size={16} />
              <span>Luo Uusi Kuponki</span>
            </button>
          </div>

          {/* Coupons List */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '14px' }}>
            {couponList.map(cpn => (
              <div
                key={cpn.id}
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '18px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  opacity: cpn.is_active ? 1 : 0.6
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div
                    style={{
                      background: 'var(--accent-copper-light)',
                      color: 'var(--accent-copper)',
                      border: '1px dashed var(--accent-copper)',
                      padding: '4px 10px',
                      borderRadius: 'var(--radius-xs)',
                      fontWeight: 800,
                      fontSize: '0.92rem',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {cpn.code}
                  </div>

                  <div style={{ fontWeight: 800, color: 'var(--accent-emerald)', fontSize: '1.05rem' }}>
                    {cpn.discount_type === 'percentage' ? `-${cpn.discount_value}%` : `-${cpn.discount_value.toFixed(2)} €`}
                  </div>
                </div>

                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{cpn.title}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '2px' }}>{cpn.description}</div>
                </div>

                {/* Conditions Badges */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                  {cpn.min_order_amount > 0 && (
                    <span style={{ background: 'var(--bg-surface)', padding: '2px 6px', borderRadius: '4px' }}>
                      Min: {cpn.min_order_amount.toFixed(2)} €
                    </span>
                  )}
                  {cpn.valid_start_time && (
                    <span style={{ background: 'var(--bg-surface)', padding: '2px 6px', borderRadius: '4px' }}>
                      🕒 {cpn.valid_start_time}–{cpn.valid_end_time}
                    </span>
                  )}
                  {cpn.festival_tag && (
                    <span style={{ background: 'rgba(245, 158, 11, 0.15)', color: 'var(--accent-amber)', padding: '2px 6px', borderRadius: '4px', fontWeight: 700 }}>
                      🎉 {cpn.festival_tag}
                    </span>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', borderTop: '1px solid var(--border-subtle)', paddingTop: '10px' }}>
                  <button
                    className="btn-secondary"
                    style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                    onClick={() => handleToggleCoupon(cpn.id)}
                  >
                    {cpn.is_active ? '✅ Aktiivinen' : '⏸️ Pois päältä'}
                  </button>

                  <button
                    className="btn-danger"
                    style={{ padding: '4px 8px' }}
                    onClick={() => handleDeleteCoupon(cpn.id)}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Modal: Create Coupon */}
          {isAddingCoupon && (
            <div className="modal-overlay" onClick={() => setIsAddingCoupon(false)}>
              <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: '540px' }}>
                <div className="modal-header">
                  <div className="modal-title">Luo Uusi Älykäs Kuponki</div>
                  <button className="modal-close-btn" onClick={() => setIsAddingCoupon(false)}>✕</button>
                </div>

                <form onSubmit={handleCreateCoupon}>
                  <div className="modal-body">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div className="form-group">
                        <label className="form-label">Kuponkikoodi (Code) *</label>
                        <input
                          type="text"
                          required
                          className="form-input"
                          style={{ textTransform: 'uppercase', fontWeight: 800 }}
                          value={newCouponCode}
                          onChange={e => setNewCouponCode(e.target.value)}
                          placeholder="Esim. LOUNAS15, VIP20"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Alennustyyppi</label>
                        <select
                          className="form-input"
                          value={newDiscountType}
                          onChange={e => setNewDiscountType(e.target.value as any)}
                        >
                          <option value="fixed">Kiinteä Euroalennus (€)</option>
                          <option value="percentage">Prosenttialennus (%)</option>
                        </select>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div className="form-group">
                        <label className="form-label">Alennuksen Arvo ({newDiscountType === 'percentage' ? '%' : '€'}) *</label>
                        <input
                          type="number"
                          step="0.1"
                          required
                          className="form-input"
                          value={newDiscountVal}
                          onChange={e => setNewDiscountVal(parseFloat(e.target.value))}
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Minimitilaus (€)</label>
                        <input
                          type="number"
                          step="0.5"
                          className="form-input"
                          value={newMinOrder}
                          onChange={e => setNewMinOrder(parseFloat(e.target.value))}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Kupongin Nimi / Otsikko</label>
                      <input
                        type="text"
                        className="form-input"
                        value={newCouponTitle}
                        onChange={e => setNewCouponTitle(e.target.value)}
                        placeholder="Esim. Arkilounas 3,50 € alennus"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Kampanja / Festivaalitunniste</label>
                      <input
                        type="text"
                        className="form-input"
                        value={newFestivalTag}
                        onChange={e => setNewFestivalTag(e.target.value)}
                        placeholder="Esim. Juhannus Special, Lounas, VIP Asiakkaat"
                      />
                    </div>

                    {/* Time of Day Conditions */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div className="form-group">
                        <label className="form-label">Alkamisaika (Valinnainen)</label>
                        <input
                          type="time"
                          className="form-input"
                          value={newStartTime}
                          onChange={e => setNewStartTime(e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Päättymisaika (Valinnainen)</label>
                        <input
                          type="time"
                          className="form-input"
                          value={newEndTime}
                          onChange={e => setNewEndTime(e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Day Selection */}
                    <div className="form-group">
                      <label className="form-label">Voimassaolopäivät (Days)</label>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {[
                          { num: 1, label: 'Ma' },
                          { num: 2, label: 'Ti' },
                          { num: 3, label: 'Ke' },
                          { num: 4, label: 'To' },
                          { num: 5, label: 'Pe' },
                          { num: 6, label: 'La' },
                          { num: 0, label: 'Su' }
                        ].map(d => (
                          <button
                            type="button"
                            key={d.num}
                            onClick={() => toggleDaySelection(d.num)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-medium)',
                              background: newValidDays.includes(d.num) ? 'var(--accent-copper)' : 'var(--bg-surface)',
                              color: newValidDays.includes(d.num) ? '#fff' : 'var(--text-secondary)',
                              fontWeight: 700,
                              fontSize: '0.8rem'
                            }}
                          >
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn-secondary" onClick={() => setIsAddingCoupon(false)}>Peruuta</button>
                    <button type="submit" className="btn-primary">Luo Kuponki</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 4. DYNAMIC MULTI-SLIDE HERO & PROMO CAROUSEL STUDIO */}
      {activeSubTab === 'hero' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Header & Add Button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Layers size={22} color="var(--accent-copper)" />
                <span>Hero & Promotional Carousel Studio ({(heroSlides || []).length} Slides)</span>
              </h3>
              <p style={{ fontSize: '0.84rem', color: 'var(--text-muted)' }}>
                Add, edit, reorder, and upload images for top rotating banners, brand stories, lunch offers, and chef specials.
              </p>
            </div>

            <button className="btn-primary" onClick={handleOpenAddSlide}>
              <Plus size={16} />
              <span>Add New Slide</span>
            </button>
          </div>

          {/* Live Simulator Preview */}
          <div
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-xl)',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ fontSize: '0.86rem', fontWeight: 800, color: 'var(--accent-copper)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Sparkles size={16} />
                <span>Live Customer Carousel Preview (Auto-Rotating & Touch-Swipable)</span>
              </div>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                Shows exactly how dining customers see your top slides on mobile & desktop
              </span>
            </div>

            {/* Live Slider Simulation */}
            <div style={{ maxWidth: '850px', width: '100%', margin: '0 auto' }}>
              <HeroSlider />
            </div>
          </div>

          {/* Slides List Cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sliders size={18} color="var(--accent-copper)" />
              <span>Configured Carousel Slides ({(heroSlides || []).length})</span>
            </h4>

            {(heroSlides || []).length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-lg)' }}>
                <p style={{ color: 'var(--text-muted)' }}>No slides created yet. Click "Add New Slide" to create your first top rotating banner!</p>
              </div>
            ) : (
              (heroSlides || []).map((slide, index) => (
                <div
                  key={slide.id}
                  style={{
                    background: 'var(--bg-secondary)',
                    border: `1px solid ${slide.is_active ? 'var(--border-medium)' : 'var(--border-subtle)'}`,
                    borderRadius: 'var(--radius-lg)',
                    padding: '16px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    opacity: slide.is_active ? 1 : 0.65,
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                    {/* Left Info */}
                    <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', flex: 1, minWidth: '280px' }}>
                      {/* Order Number Badge */}
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: 'var(--bg-surface)',
                          border: '1px solid var(--border-medium)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 800,
                          fontSize: '0.82rem',
                          color: 'var(--text-secondary)',
                          flexShrink: 0
                        }}
                      >
                        #{index + 1}
                      </div>

                      {/* Image Thumbnail */}
                      {slide.image_url && (
                        <img
                          src={slide.image_url}
                          alt={slide.title_en}
                          style={{
                            width: '90px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--border-subtle)',
                            flexShrink: 0
                          }}
                        />
                      )}

                      {/* Text Details */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span
                            style={{
                              background: 'var(--accent-copper)',
                              color: '#fff',
                              padding: '2px 8px',
                              borderRadius: 'var(--radius-xs)',
                              fontSize: '0.72rem',
                              fontWeight: 800
                            }}
                          >
                            {slide.badge_text || slide.slide_type}
                          </span>

                          <span
                            style={{
                              background: 'var(--bg-surface)',
                              color: 'var(--text-muted)',
                              padding: '2px 7px',
                              borderRadius: 'var(--radius-xs)',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              textTransform: 'uppercase'
                            }}
                          >
                            Type: {slide.slide_type.replace('_', ' ')}
                          </span>

                          {slide.discount_code && (
                            <span
                              style={{
                                background: 'rgba(224, 90, 54, 0.15)',
                                color: 'var(--accent-copper)',
                                padding: '2px 8px',
                                borderRadius: 'var(--radius-xs)',
                                fontSize: '0.72rem',
                                fontWeight: 800,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <Tag size={11} />
                              <span>Code: {slide.discount_code}</span>
                            </span>
                          )}
                        </div>

                        <div style={{ fontWeight: 800, fontSize: '0.96rem', color: 'var(--text-primary)', marginTop: '2px' }}>
                          {slide.title_en}
                        </div>

                        {slide.title_fi && slide.title_fi !== slide.title_en && (
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                            🇫🇮 {slide.title_fi}
                          </div>
                        )}

                        {slide.subtitle_en && (
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.35 }}>
                            {slide.subtitle_en}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      {/* Active Status Toggle */}
                      <button
                        type="button"
                        className={slide.is_active ? 'btn-primary' : 'btn-secondary'}
                        style={{ padding: '6px 12px', fontSize: '0.76rem', gap: '5px' }}
                        onClick={() => handleToggleSlideActive(slide.id, slide.is_active)}
                      >
                        <Check size={13} />
                        <span>{slide.is_active ? 'Active on Menu' : 'Hidden'}</span>
                      </button>

                      {/* Edit Button */}
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ padding: '6px 12px', fontSize: '0.76rem', gap: '5px' }}
                        onClick={() => handleOpenEditSlide(slide)}
                      >
                        <Edit2 size={13} />
                        <span>Edit</span>
                      </button>

                      {/* Delete Button */}
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ padding: '6px 10px', color: 'var(--accent-rose)', borderColor: 'rgba(244, 63, 94, 0.3)' }}
                        onClick={() => handleDeleteSlide(slide.id)}
                        title="Remove slide"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Add / Edit Slide Modal (Canva/Shopify Style 2-Column Visual Builder) */}
          {isSlideModalOpen && (
            <div className="modal-backdrop">
              <div
                className="modal-content"
                style={{
                  maxWidth: '980px',
                  width: '95%',
                  maxHeight: '92vh',
                  display: 'flex',
                  flexDirection: 'column',
                  padding: 0,
                  overflow: 'hidden'
                }}
              >
                {/* Modal Header */}
                <div
                  className="modal-header"
                  style={{
                    padding: '18px 24px',
                    borderBottom: '1px solid var(--border-subtle)',
                    background: 'var(--bg-secondary)',
                    margin: 0
                  }}
                >
                  <div>
                    <h3 style={{ fontSize: '1.2rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Layers size={20} color="var(--accent-copper)" />
                      <span>{editingSlide ? 'Edit Carousel Slide' : 'Create New Carousel Slide'}</span>
                    </h3>
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '2px 0 0 0' }}>
                      Simple visual banner maker for lunch deals, promotions, and chef specials.
                    </p>
                  </div>
                  <button className="modal-close-btn" onClick={() => setIsSlideModalOpen(false)}>×</button>
                </div>

                <form onSubmit={handleSaveSlide} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                  {/* Modal Body: 2-Column Layout */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.15fr 0.85fr',
                      gap: '24px',
                      padding: '24px',
                      overflowY: 'auto',
                      flex: 1
                    }}
                  >
                    {/* Left Column: Easy Visual Form */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                      {/* 1. Quick Presets (1-Tap Auto Fill) */}
                      <div>
                        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Sparkles size={14} color="var(--accent-copper)" />
                          <span>Quick 1-Tap Presets (Click to autofill):</span>
                        </label>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                          {[
                            {
                              label: '🍕 Lunch Deal 14.90 €',
                              type: 'promo_offer',
                              badge: '🎁 LOUNASTARJOUS 11–14',
                              title: 'Lunch Special 11–14: Any Pizza + Spring Water only 14.90 €',
                              title_fi: 'Lounas klo 11–14: Valinnainen pizza + Lähdevesi 14,90 €',
                              sub: 'Valid Mon–Fri for table orders. Free coffee & side salad.',
                              sub_fi: 'Voimassa ma–pe klo 11–14 pöytätilauksiin.',
                              code: 'LUNCH1490',
                              img: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=1200&q=80'
                            },
                            {
                              label: '🔥 Wood Oven Story',
                              type: 'brand_hero',
                              badge: '🔥 450°C WOOD OVEN',
                              title: 'Authentic 48h Fermented Artisan Sourdough Pizzas',
                              title_fi: 'Aito 48h Hapanjuuritaikina & 450°C Puu-uuni',
                              sub: 'Baked in seconds with crisp leopard crust and fresh Finnish ingredients.',
                              sub_fi: 'Paistettu sekunneissa rapeaksi aidoilla suomalaisilla raaka-aineilla.',
                              code: '',
                              img: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=1400&q=80'
                            },
                            {
                              label: '⭐ Chef Truffle Special',
                              type: 'dish_highlight',
                              badge: '⭐ CHEF SPECIALTY',
                              title: 'Nordic Reindeer & Lapland Porcini Truffle Sourdough Pizza',
                              title_fi: 'Lapin Poro & Villitatti-Tryffelipizza',
                              sub: 'Smoked Lapland reindeer, wild porcini truffle cream & Fior di Latte mozzarella.',
                              sub_fi: 'Savustettua Lapin poroa ja villitatti-tryffelikreemiä.',
                              code: '',
                              img: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&w=1200&q=80'
                            }
                          ].map((preset, pIdx) => (
                            <button
                              key={pIdx}
                              type="button"
                              onClick={() => {
                                setSlideType(preset.type as any);
                                setSlideBadge(preset.badge);
                                setSlideTitleEn(preset.title);
                                setSlideTitleFi(preset.title_fi);
                                setSlideSubtitleEn(preset.sub);
                                setSlideSubtitleFi(preset.sub_fi);
                                setSlideDiscountCode(preset.code);
                                setSlideImageUrl(preset.img);
                              }}
                              style={{
                                background: 'var(--bg-surface)',
                                border: '1px solid var(--border-medium)',
                                padding: '4px 10px',
                                borderRadius: 'var(--radius-full)',
                                fontSize: '0.74rem',
                                fontWeight: 700,
                                color: 'var(--text-secondary)',
                                cursor: 'pointer',
                                transition: 'all var(--transition-fast)'
                              }}
                            >
                              {preset.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* 2. Slide Type Cards */}
                      <div className="form-group">
                        <label className="form-label">1. Choose Slide Type</label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                          {[
                            { type: 'promo_offer', label: 'Promo Offer', icon: <Gift size={16} />, desc: 'Discount voucher + coupon' },
                            { type: 'brand_hero', label: 'Brand Story', icon: <Flame size={16} />, desc: 'Quality badges & intro' },
                            { type: 'dish_highlight', label: 'Chef Special', icon: <Utensils size={16} />, desc: 'Star dish highlight' }
                          ].map(tItem => (
                            <div
                              key={tItem.type}
                              onClick={() => setSlideType(tItem.type as any)}
                              style={{
                                background: slideType === tItem.type ? 'rgba(224, 90, 54, 0.12)' : 'var(--bg-secondary)',
                                border: `2px solid ${slideType === tItem.type ? 'var(--accent-copper)' : 'var(--border-subtle)'}`,
                                borderRadius: 'var(--radius-md)',
                                padding: '10px 8px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '3px',
                                cursor: 'pointer',
                                textAlign: 'center',
                                alignItems: 'center',
                                transition: 'all var(--transition-fast)'
                              }}
                            >
                              <div style={{ color: slideType === tItem.type ? 'var(--accent-copper)' : 'var(--text-secondary)' }}>
                                {tItem.icon}
                              </div>
                              <div style={{ fontWeight: 800, fontSize: '0.78rem', color: slideType === tItem.type ? 'var(--accent-copper)' : 'var(--text-primary)' }}>
                                {tItem.label}
                              </div>
                              <div style={{ fontSize: '0.66rem', color: 'var(--text-muted)' }}>{tItem.desc}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* 3. Badge Selection / Input */}
                      <div className="form-group">
                        <label className="form-label">2. Badge Pill (Shown on top of banner)</label>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                          {['🎁 LUNCH SPECIAL', '🔥 450°C WOOD OVEN', '⭐ CHEF SPECIALTY', '🍕 FRESH SOURDOUGH', '✨ WEEKEND DEAL'].map((bdg, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setSlideBadge(bdg)}
                              style={{
                                padding: '3px 8px',
                                borderRadius: 'var(--radius-xs)',
                                border: '1px solid var(--border-subtle)',
                                background: slideBadge === bdg ? 'var(--accent-copper)' : 'var(--bg-surface)',
                                color: slideBadge === bdg ? '#fff' : 'var(--text-secondary)',
                                fontSize: '0.7rem',
                                fontWeight: 800,
                                cursor: 'pointer'
                              }}
                            >
                              {bdg}
                            </button>
                          ))}
                        </div>
                        <input
                          type="text"
                          className="form-input"
                          value={slideBadge}
                          onChange={e => setSlideBadge(e.target.value)}
                          placeholder="Or type custom badge..."
                        />
                      </div>

                      {/* 4. Headline Title */}
                      <div className="form-group">
                        <label className="form-label">3. Headline Title *</label>
                        <input
                          type="text"
                          className="form-input"
                          required
                          value={slideTitleEn}
                          onChange={e => setSlideTitleEn(e.target.value)}
                          placeholder="e.g. Lunch Special 11–14: Any Pizza only 14.90 €"
                        />
                      </div>

                      {/* 5. Subtitle */}
                      <div className="form-group">
                        <label className="form-label">4. Details / Subtitle (Optional)</label>
                        <input
                          type="text"
                          className="form-input"
                          value={slideSubtitleEn}
                          onChange={e => setSlideSubtitleEn(e.target.value)}
                          placeholder="e.g. Valid Mon–Fri for dine-in table orders"
                        />
                      </div>

                      {/* 6. Photo Upload Card */}
                      <div className="form-group">
                        <label className="form-label">5. Banner Background Photo</label>
                        <div
                          style={{
                            border: '2px dashed var(--border-medium)',
                            borderRadius: 'var(--radius-md)',
                            padding: '14px',
                            background: 'var(--bg-secondary)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '10px',
                            textAlign: 'center'
                          }}
                        >
                          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                            <label className="btn-primary" style={{ cursor: 'pointer', padding: '8px 16px', fontSize: '0.82rem', gap: '6px' }}>
                              <Upload size={15} />
                              <span>Upload Photo from Device</span>
                              <input type="file" accept="image/*" onChange={handleSlideImageFileUpload} style={{ display: 'none' }} />
                            </label>

                            {slideImageUrl && (
                              <button
                                type="button"
                                className="btn-secondary"
                                style={{ padding: '8px 12px', fontSize: '0.78rem', color: 'var(--accent-rose)' }}
                                onClick={() => setSlideImageUrl('')}
                              >
                                Clear Photo
                              </button>
                            )}
                          </div>

                          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                            Or paste direct image URL below:
                          </span>

                          <input
                            type="url"
                            className="form-input"
                            style={{ width: '100%', fontSize: '0.8rem' }}
                            value={slideImageUrl}
                            onChange={e => setSlideImageUrl(e.target.value)}
                            placeholder="https://images.unsplash.com/photo-..."
                          />
                        </div>
                      </div>

                      {/* 7. Link Coupon (1-Tap Selector) */}
                      {slideType === 'promo_offer' && (
                        <div className="form-group">
                          <label className="form-label">6. Link Discount Voucher (Auto-Apply on Tap)</label>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                            {(coupons || []).map(cp => (
                              <button
                                key={cp.id}
                                type="button"
                                onClick={() => setSlideDiscountCode(cp.code)}
                                style={{
                                  padding: '3px 9px',
                                  borderRadius: 'var(--radius-xs)',
                                  border: '1px solid var(--border-subtle)',
                                  background: slideDiscountCode === cp.code ? '#ffffff' : 'var(--bg-surface)',
                                  color: slideDiscountCode === cp.code ? '#b91c1c' : 'var(--text-secondary)',
                                  fontWeight: 800,
                                  fontSize: '0.72rem',
                                  cursor: 'pointer'
                                }}
                              >
                                🎟️ {cp.code} ({cp.discount_type === 'percentage' ? `${cp.discount_value}%` : `${cp.discount_value} €`})
                              </button>
                            ))}
                          </div>
                          <input
                            type="text"
                            className="form-input"
                            value={slideDiscountCode}
                            onChange={e => setSlideDiscountCode(e.target.value.toUpperCase())}
                            placeholder="Or type coupon code (e.g. LUNCH1490)"
                          />
                        </div>
                      )}

                      {/* Optional Finnish Translations (Collapsible) */}
                      <details style={{ background: 'var(--bg-secondary)', padding: '12px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                          🇫🇮 Add Finnish Language Translations (Optional)
                        </summary>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
                          <div>
                            <label className="form-label" style={{ fontSize: '0.76rem' }}>Otsikko Suomeksi</label>
                            <input
                              type="text"
                              className="form-input"
                              value={slideTitleFi}
                              onChange={e => setSlideTitleFi(e.target.value)}
                              placeholder="Lounas klo 11–14: Valinnainen pizza 14,90 €"
                            />
                          </div>
                          <div>
                            <label className="form-label" style={{ fontSize: '0.76rem' }}>Alaotsikko Suomeksi</label>
                            <input
                              type="text"
                              className="form-input"
                              value={slideSubtitleFi}
                              onChange={e => setSlideSubtitleFi(e.target.value)}
                              placeholder="Voimassa ma–pe klo 11–14 pöytätilauksiin"
                            />
                          </div>
                        </div>
                      </details>
                    </div>

                    {/* Right Column: Live Interactive Card Preview Mockup */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ fontSize: '0.84rem', fontWeight: 800, color: 'var(--accent-copper)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Smartphone size={16} />
                          <span>Live Customer Preview</span>
                        </div>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Updates as you type</span>
                      </div>

                      {/* Live Card Preview Box */}
                      <div
                        style={{
                          borderRadius: 'var(--radius-lg)',
                          overflow: 'hidden',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                          border: '1px solid var(--border-medium)',
                          background: slideImageUrl
                            ? `linear-gradient(180deg, rgba(15, 17, 21, 0.3) 0%, rgba(15, 17, 21, 0.88) 100%), url(${slideImageUrl}) center/cover no-repeat`
                            : 'linear-gradient(135deg, rgba(224, 90, 54, 0.95), rgba(185, 28, 28, 0.92))',
                          minHeight: '220px',
                          padding: '18px 16px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-end',
                          gap: '8px',
                          color: '#fff',
                          position: 'relative'
                        }}
                      >
                        {/* Top Badge & Code */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '8px' }}>
                          <span
                            style={{
                              background: 'rgba(255, 255, 255, 0.22)',
                              backdropFilter: 'blur(8px)',
                              padding: '3px 8px',
                              borderRadius: 'var(--radius-xs)',
                              fontSize: '0.7rem',
                              fontWeight: 800,
                              textTransform: 'uppercase'
                            }}
                          >
                            {slideBadge || '🔥 SPECIAL'}
                          </span>

                          {slideDiscountCode && (
                            <span
                              style={{
                                background: '#ffffff',
                                color: '#b91c1c',
                                padding: '3px 8px',
                                borderRadius: 'var(--radius-full)',
                                fontSize: '0.72rem',
                                fontWeight: 800,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <Tag size={11} />
                              <span>{slideDiscountCode}</span>
                            </span>
                          )}
                        </div>

                        {/* Title */}
                        <div style={{ fontWeight: 900, fontSize: '1.15rem', lineHeight: 1.25, color: '#ffffff', textShadow: '0 2px 6px rgba(0,0,0,0.6)' }}>
                          {slideTitleEn || 'Your Banner Title Goes Here'}
                        </div>

                        {/* Subtitle */}
                        {slideSubtitleEn && (
                          <div style={{ fontSize: '0.78rem', opacity: 0.92, lineHeight: 1.35, textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
                            {slideSubtitleEn}
                          </div>
                        )}

                        {/* CTA / Tap Hint */}
                        {slideDiscountCode && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.72rem', opacity: 0.9, marginTop: '2px' }}>
                            <Sparkles size={12} />
                            <span>Tap banner to automatically apply {slideDiscountCode} ✨</span>
                          </div>
                        )}
                      </div>

                      {/* Help Tip Card */}
                      <div
                        style={{
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 'var(--radius-md)',
                          padding: '12px 14px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px'
                        }}
                      >
                        <div style={{ fontWeight: 800, fontSize: '0.78rem', color: 'var(--accent-copper)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <Info size={14} />
                          <span>Tips for high-converting slides:</span>
                        </div>
                        <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '0.74rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
                          <li>Keep titles short and mention exact prices (e.g. <em>14.90 €</em>).</li>
                          <li>Upload crisp, warm food photos (landscape 750×300px recommended).</li>
                          <li>Link voucher codes for 1-tap discounts at customer checkout.</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div
                    className="modal-footer"
                    style={{
                      padding: '16px 24px',
                      borderTop: '1px solid var(--border-subtle)',
                      background: 'var(--bg-secondary)',
                      margin: 0
                    }}
                  >
                    <button type="button" className="btn-secondary" onClick={() => setIsSlideModalOpen(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary" style={{ padding: '10px 24px', fontSize: '0.9rem' }}>
                      {editingSlide ? 'Save Changes' : 'Publish to Carousel'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
